import { useState, useEffect, useCallback } from 'react';

// Define the structure for cookie preferences
export interface CookiePreferences {
  essential: true; // Essential cookies are always enabled
  analytics: boolean;
  marketing: boolean;
  lastSet: string | null; // ISO string of when preferences were last set
}

// Define the structure for the hook's return value
export interface UseCookieConsentReturn {
  preferences: CookiePreferences;
  hasMadeChoice: boolean; // True if user has made a choice (preferences stored)
  isLoading: boolean; // True while loading preferences from localStorage
  acceptAllCookies: () => void;
  rejectAllCookies: () => void;
  saveCustomPreferences: (customPrefs: Partial<Omit<CookiePreferences, 'essential' | 'lastSet'>>) => void;
  isCategoryEnabled: (category: keyof Omit<CookiePreferences, 'essential' | 'lastSet'>) => boolean;
}

const LOCAL_STORAGE_KEY = 'cookie_consent_preferences';

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  lastSet: null,
};

export const useCookieConsent = (): UseCookieConsentReturn => {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasMadeChoice, setHasMadeChoice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    try {
      const storedPrefsString = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedPrefsString) {
        const storedPrefs = JSON.parse(storedPrefsString) as CookiePreferences;
        // Basic validation for stored preferences
        if (typeof storedPrefs === 'object' && storedPrefs !== null && 
            typeof storedPrefs.analytics === 'boolean' &&
            typeof storedPrefs.marketing === 'boolean') {
          setPreferences({ ...defaultPreferences, ...storedPrefs, essential: true });
          setHasMadeChoice(true);
        } else {
          // Malformed data, reset to default and clear storage
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          setPreferences(defaultPreferences);
          setHasMadeChoice(false);
        }
      } else {
        setPreferences(defaultPreferences);
        setHasMadeChoice(false);
      }
    } catch (error) {
      console.warn('Failed to load cookie preferences from localStorage:', error);
      // Fallback to most restrictive if localStorage is unavailable or fails
      localStorage.removeItem(LOCAL_STORAGE_KEY); // Also remove malformed item on parse error
      setPreferences(defaultPreferences);
      setHasMadeChoice(false); 
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updatePreferences = useCallback((newPrefs: CookiePreferences) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPrefs));
      setPreferences(newPrefs);
      setHasMadeChoice(true);
    } catch (error) {
      console.error('Failed to save cookie preferences to localStorage:', error);
      // Potentially show a non-intrusive error to the user here if UI is available
      // For now, we just log it. The state will not be persisted.
      setPreferences(newPrefs); // Keep state in memory for current session if storage fails
      setHasMadeChoice(true); // Assume choice is made even if not persisted for current session UX
    }
  }, []);

  const acceptAllCookies = useCallback(() => {
    const newPrefs: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      lastSet: new Date().toISOString(),
    };
    updatePreferences(newPrefs);
  }, [updatePreferences]);

  const rejectAllCookies = useCallback(() => {
    const newPrefs: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      lastSet: new Date().toISOString(),
    };
    updatePreferences(newPrefs);
  }, [updatePreferences]);

  const saveCustomPreferences = useCallback((customPrefs: Partial<Omit<CookiePreferences, 'essential' | 'lastSet'>>) => {
    const newPrefs: CookiePreferences = {
      ...preferences, // Start with current preferences
      ...customPrefs, // Override with custom choices
      essential: true, // Ensure essential is always true
      lastSet: new Date().toISOString(),
    };
    updatePreferences(newPrefs);
  }, [preferences, updatePreferences]);

  const isCategoryEnabled = useCallback((category: keyof Omit<CookiePreferences, 'essential' | 'lastSet'>): boolean => {
    return preferences[category] || false;
  }, [preferences]);

  return {
    preferences,
    hasMadeChoice,
    isLoading,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomPreferences,
    isCategoryEnabled,
  };
};
